import type { TargetAudience } from '../../../models/MasterAdmin/pushNotificationCampaign';

export type AudienceCategory = 'user' | 'doctor' | 'ambulance' | 'delivery_partner' | 'vendor';

export const USER_AUDIENCES: { value: TargetAudience; label: string }[] = [
  { value: 'ALL_USERS', label: 'Registered & logged in' },
  { value: 'UNREGISTERED_GUESTS', label: 'Unregistered Guests' },
  { value: 'SINGLE_USER', label: 'Single Specific User' },
  { value: 'INACTIVE_USERS', label: 'Registered but Inactive Users' },
  { value: 'USERS_WITHOUT_HEALTH_RECORD', label: 'Users without Health Records' },
  { value: 'LOGGED_OUT_USERS', label: 'Logged out after registration' },
];

export const DOCTOR_AUDIENCES: { value: TargetAudience; label: string }[] = [
  { value: 'DOCTORS', label: 'Doctors (All)' },
  { value: 'INACTIVE_DOCTORS', label: 'Doctors (Inactive - 30+ days)' },
  { value: 'PENDING_APPROVAL_DOCTORS', label: 'Doctors (Pending Approval)' },
  { value: 'LOGGED_OUT_DOCTORS', label: 'Doctors (Logged-out)' },
  { value: 'SINGLE_DOCTOR', label: 'Single Specific Doctor' },
];

export const AMBULANCE_AUDIENCES: { value: TargetAudience; label: string }[] = [
  { value: 'AMBULANCE', label: 'Ambulance (All)' },
  { value: 'INACTIVE_AMBULANCE', label: 'Ambulance (Inactive - 30+ days)' },
  { value: 'PENDING_APPROVAL_AMBULANCE', label: 'Ambulance (Pending Approval)' },
  { value: 'LOGGED_OUT_AMBULANCE', label: 'Ambulance (Logged-out)' },
  { value: 'SINGLE_AMBULANCE', label: 'Single Specific Ambulance' },
];

export const DELIVERY_PARTNER_AUDIENCES: { value: TargetAudience; label: string }[] = [
  { value: 'DELIVERY_PARTNERS', label: 'Delivery Partners' },
];

export const GENERAL_VENDOR_AUDIENCES: { value: TargetAudience; label: string }[] = [
  { value: 'VENDORS', label: 'Vendors (All)' },
  { value: 'INACTIVE_VENDORS', label: 'Inactive Vendors (30+ days)' },
  { value: 'LOGGED_OUT_VENDORS', label: 'Vendors (Logged-out)' },
  { value: 'PENDING_APPROVAL_VENDORS', label: 'Pending Approval Vendors' },
  { value: 'SINGLE_VENDOR', label: 'Single Specific Vendor' },
];

export const AUDIENCE_HELPER_NOTES: Record<TargetAudience, string> = {
  ALL_USERS: 'Registered users with an active login session. Push uses their active user FCM token.',
  VENDORS: 'This message will be sent to all active logged-in vendors.',
  DELIVERY_PARTNERS: 'This message will be sent to all active logged-in delivery partners.',
  DOCTORS: 'This message will be sent to all onboarded doctors via their vendor FCM tokens.',
  AMBULANCE: 'This message will be sent to all onboarded ambulance agencies via their vendor FCM tokens.',
  INACTIVE_VENDORS: 'Vendors who have not logged in or had any activity in the last 30 days.',
  LOGGED_OUT_VENDORS: 'Registered vendors who have logged out or have the app closed.',
  PENDING_APPROVAL_VENDORS: 'Registered vendors whose account status is still pending approval.',
  INACTIVE_DOCTORS: 'Onboarded doctors who have not logged in or had any activity in the last 30 days.',
  PENDING_APPROVAL_DOCTORS: 'Doctors whose vendor profile is still pending approval.',
  LOGGED_OUT_DOCTORS: 'Registered doctors who have logged out or have the app closed.',
  INACTIVE_AMBULANCE: 'Ambulance agencies that have not logged in or had any activity in the last 30 days.',
  PENDING_APPROVAL_AMBULANCE: 'Ambulance agencies whose vendor profile is still pending approval.',
  LOGGED_OUT_AMBULANCE: 'Registered ambulance agencies who have logged out or have the app closed.',
  UNREGISTERED_GUESTS: 'First-time users who installed the app but never registered.',
  LOGGED_OUT_USERS: 'Registered users with no active session.',
  SINGLE_USER: 'Opens a recipient picker — choose by name and phone (not raw user id).',
  INACTIVE_USERS: 'This message will be sent to registered users who have been inactive.',
  USERS_WITHOUT_HEALTH_RECORD: 'Registered users without health records.',
  SINGLE_DOCTOR: 'Opens a recipient picker — choose doctor by name and phone.',
  SINGLE_AMBULANCE: 'Opens a recipient picker — choose ambulance by name and phone.',
  SINGLE_VENDOR: 'Opens a recipient picker — choose vendor by name and phone.',
};

export const SCHEDULE_TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'UTC', label: 'UTC' },
];

export const TARGET_PLATFORMS = [
  { value: 'BOTH', label: 'Android & iOS' },
  { value: 'ANDROID', label: 'Android only' },
  { value: 'IOS', label: 'iOS only' },
  { value: 'WEB', label: 'Web' },
] as const;

export const PUSH_PLACEHOLDER_HINTS = [
  { key: 'userName', label: 'User name' },
  { key: 'firstName', label: 'First name' },
  { key: 'city', label: 'City' },
] as const;

export function audiencesForCategory(cat: AudienceCategory) {
  if (cat === 'doctor') return DOCTOR_AUDIENCES;
  if (cat === 'ambulance') return AMBULANCE_AUDIENCES;
  if (cat === 'delivery_partner') return DELIVERY_PARTNER_AUDIENCES;
  if (cat === 'vendor') return GENERAL_VENDOR_AUDIENCES;
  return USER_AUDIENCES;
}

export function categoryForAudience(aud: TargetAudience): AudienceCategory {
  if (['DOCTORS', 'INACTIVE_DOCTORS', 'PENDING_APPROVAL_DOCTORS', 'LOGGED_OUT_DOCTORS', 'SINGLE_DOCTOR'].includes(aud)) {
    return 'doctor';
  }
  if (['AMBULANCE', 'INACTIVE_AMBULANCE', 'PENDING_APPROVAL_AMBULANCE', 'LOGGED_OUT_AMBULANCE', 'SINGLE_AMBULANCE'].includes(aud)) {
    return 'ambulance';
  }
  if (aud === 'DELIVERY_PARTNERS') return 'delivery_partner';
  if (['VENDORS', 'INACTIVE_VENDORS', 'LOGGED_OUT_VENDORS', 'PENDING_APPROVAL_VENDORS', 'SINGLE_VENDOR'].includes(aud)) {
    return 'vendor';
  }
  return 'user';
}

export const PUSH_AUDIENCE_PILLS: { value: TargetAudience; label: string }[] = [
  ...USER_AUDIENCES,
  ...DOCTOR_AUDIENCES,
  ...AMBULANCE_AUDIENCES,
  ...DELIVERY_PARTNER_AUDIENCES,
  ...GENERAL_VENDOR_AUDIENCES,
];

export function isPushPickerAudience(aud: TargetAudience): boolean {
  return ['SINGLE_USER', 'SINGLE_DOCTOR', 'SINGLE_AMBULANCE', 'SINGLE_VENDOR'].includes(aud);
}

export const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white';
